
import { useCallback, useReducer } from "react";
import _ from "lodash";
import { IChartCountModel } from "./NotificationsDetailChartUtils";
import { IeLynxObject } from "../../../../common/models/eLynxObject";


export type NotificationsDetailChartStateType = {
    chartData: IChartCountModel[] | undefined,
    selectedIndex: number | undefined,
    sortExpression: string,
    sortAscending: boolean,
    sortReset: boolean,
    target: IeLynxObject | undefined,
    originalTarget: IeLynxObject | undefined,
    originalTargetSet: boolean
};

export let defaultNotificationsDetailChartState: NotificationsDetailChartStateType = {
    chartData: undefined,
    selectedIndex: undefined,
    sortExpression: 'Text',
    sortAscending: false,
    sortReset: false,
    target: undefined,
    originalTarget: undefined,
    originalTargetSet: false
};

enum NotificationsDetailChartStateActionType {

    ChartDataChanged = 'NOTIFICATIONS_DETAIL_CHART_DATA_CHANGED',
    SelectedIndexChanged = 'NOTIFICATIONS_DETAIL_SELECTED_INDEX_CHANGED',
    SortExpressionChanged = 'NOTIFICATIONS_DETAIL_SORT_EXPRESSION_CHANGED',
    SortAscendingChanged = 'NOTIFICATIONS_DETAIL_SORT_ASCENDING_CHANGED',
    SortResetChanged = 'NOTIFICATIONS_DETAIL_SORT_RESET_CHANGED',
    TargetChanged = 'NOTIFICATIONS_DETAIL_TARGET_CHANGED'
}

type NotificationsDetailChartStateActions = {
    type: NotificationsDetailChartStateActionType.ChartDataChanged;
    payload: IChartCountModel[] | undefined;
} | {
    type: NotificationsDetailChartStateActionType.SelectedIndexChanged;
    payload: number | undefined;
} | {
    type: NotificationsDetailChartStateActionType.SortExpressionChanged;
    payload: string;
} | {
    type: NotificationsDetailChartStateActionType.SortAscendingChanged;
    payload: boolean;
} | {
    type: NotificationsDetailChartStateActionType.TargetChanged;
    payload: IeLynxObject | undefined;
} | {
    type: NotificationsDetailChartStateActionType.SortResetChanged;
    payload: boolean;
};


function NotificationsDetailChartStateReducer(state: NotificationsDetailChartStateType, action: NotificationsDetailChartStateActions): NotificationsDetailChartStateType {

    const copy = _.cloneDeep(state);

    switch (action.type) {
        
        case NotificationsDetailChartStateActionType.ChartDataChanged:
            copy.chartData = action.payload;
            break;

        case NotificationsDetailChartStateActionType.SelectedIndexChanged:
            copy.selectedIndex = action.payload;
            break;

        case NotificationsDetailChartStateActionType.SortExpressionChanged:
            copy.sortExpression = action.payload;
            break;

        case NotificationsDetailChartStateActionType.SortAscendingChanged:
            copy.sortAscending = action.payload;
            break;

        case NotificationsDetailChartStateActionType.TargetChanged:
            copy.target = action.payload;
            if (!copy.originalTargetSet) {
                copy.originalTarget = action.payload;
                copy.originalTargetSet = true;
            }
            break;
                            
        case NotificationsDetailChartStateActionType.SortResetChanged:
            copy.sortReset = action.payload;
            break;

        default:
            return state;
    }

    return copy;
}

export default function useNotificationsDetailChartState() {

    const [state, dispatch] = useReducer(NotificationsDetailChartStateReducer, {...defaultNotificationsDetailChartState});

    const setChartData = useCallback((value: IChartCountModel[] | undefined) => dispatch({
        type: NotificationsDetailChartStateActionType.ChartDataChanged,
        payload: value
    }), []);


    const setSelectedIndex = useCallback((value: number | undefined) => dispatch({
        type: NotificationsDetailChartStateActionType.SelectedIndexChanged,
        payload: value
    }), []);

    const setSortExpression = useCallback((value: string) => dispatch({
        type: NotificationsDetailChartStateActionType.SortExpressionChanged,
        payload: value
    }), []);

    const setSortAscending = useCallback((value: boolean) => dispatch({
        type: NotificationsDetailChartStateActionType.SortAscendingChanged,
        payload: value
    }), []);

    const setTarget = useCallback((value: IeLynxObject | undefined) => dispatch({
        type: NotificationsDetailChartStateActionType.TargetChanged,
        payload: value
    }), []);


    const resetSortState = useCallback((value: boolean) => dispatch({
        type: NotificationsDetailChartStateActionType.SortResetChanged,
        payload: value
    }), []);


    return {
        state,
        setChartData,
        setSelectedIndex,
        setSortExpression,
        setSortAscending,
        setTarget,
        resetSortState
    };
}
